/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react";
import apiService from "services/apiService";
import { storageService } from "services/storageService";

/* ────────────────────────────────────────────────────────────
   Subscription status for the organisation currently in session.

   WHY THIS EXISTS
   The plan banner and the account menu's "Upgrade Plan" entry both need the
   live plan. They used to derive it independently — the banner from its own
   request, the menu from sessionStorage — and sessionStorage keeps whatever the
   Upgrade Plan / Pending Invoice flow last wrote there. Coming back from
   Pending Invoice and switching organisation therefore left the menu reading a
   plan that belonged to the previous organisation, so "Upgrade Plan" kept
   showing for an ENTERPRISE tenant.

   Both now read this one hook, which is keyed on the active organisation and
   refetched whenever the session's token changes.
──────────────────────────────────────────────────────────── */

/**
 * GET api/subscription/status. Only `showBanner` is required — the plan and the
 * remaining days are read from it when present, and callers fall back to the
 * JWT payload, so this keeps working whatever subset the API returns.
 */
export interface SubscriptionStatus {
    showBanner?: boolean;
    plan?: string;
    planType?: string;
    /** Days remaining, rendered in the banner text. */
    daysLeft?: number | string;
    daysRemaining?: number | string;
    subscriptionEnd?: string;
}

/** Session keys that describe one organisation's plan and must not outlive it. */
const ORG_SCOPED_SESSION_KEYS = ["plan", "initialPlanName", "schemaName", "userId", "registrationId"];

/**
 * Drops the plan hand-off values written for PlanSelection / PendingInvoices.
 * NOTE: without this, navigating to Pending Invoice and switching organisation
 * leaves the previous organisation's plan in session for the next read.
 */
export const clearOrgScopedPlanSession = () => {
    ORG_SCOPED_SESSION_KEYS.forEach((key) => sessionStorage.removeItem(key));
};

interface UseSubscriptionStatusOptions {
    /** Skipped entirely when false (e.g. non-admins never see plan UI). */
    enabled: boolean;
}

export const useSubscriptionStatus = ({ enabled }: UseSubscriptionStatusOptions) => {
    const [status, setStatus] = useState<SubscriptionStatus | null>(null);
    const [refreshKey, setRefreshKey] = useState(0);

    /**
     * The organisation the session points at. Switching organisation replaces
     * the access token in storage without remounting the shell, so this is what
     * signals that the subscription has to be looked up again.
     */
    const activeSubdomain = storageService.getItem("activeSubdomain") || "";

    /**
     * The nav-bar organisation switcher dispatches "auth-token-updated" after
     * storing the new token (services/apiService does the same after a refresh),
     * which is the reliable signal for "the session moved to another org".
     */
    useEffect(() => {
        const handleAuthTokenUpdated = () => setRefreshKey((prev) => prev + 1);

        window.addEventListener("auth-token-updated", handleAuthTokenUpdated);
        return () => {
            window.removeEventListener("auth-token-updated", handleAuthTokenUpdated);
        };
    }, []);

    useEffect(() => {
        if (!enabled) {
            setStatus(null);
            return;
        }

        let cancelled = false;

        // Drop the previous organisation's answer while the new one loads, so no
        // consumer can act on a plan that belongs to another organisation
        setStatus(null);

        apiService
            .get("api/subscription/status")
            .then((res) => {
                if (!cancelled) setStatus(res?.data || null);
            })
            .catch((err) => {
                console.log("subscription status error", err);
                if (!cancelled) setStatus(null);
            });

        return () => {
            cancelled = true;
        };
    }, [enabled, activeSubdomain, refreshKey]);

    return { status, activeSubdomain };
};

export default useSubscriptionStatus;
